import React, { useEffect, useState, useRef } from 'react';
import AdService from './AdService';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://nethajiprime-backend.onrender.com';

/**
 * AdUnit - renders a "direct" house ad for a given placement (banner | preroll | lband).
 * Respects AdService frequency cap, premium-user exclusion, and global on/off switch.
 * Tracks impressions and clicks via the existing /api/ads/{id}/impression and /click endpoints.
 *
 * Usage: <AdUnit placement="banner" isPaidUser={hasAccess} />
 */
export default function AdUnit({ placement = 'banner', isPaidUser = false }) {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const impressionFired = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadAd() {
      // Respect global config first (pulls ads_enabled flag from backend)
      await AdService.fetchGlobalConfig(API_URL);

      if (!AdService.shouldShowAds(isPaidUser)) { setLoading(false); return; }
      if (!AdService.isPlacementEnabled(placement)) { setLoading(false); return; }

      try {
        const r = await fetch(`${API_URL}/api/ads?active_only=true`);
        const d = await r.json();
        const candidates = (d.ads || []).filter(a => a.type === placement);
        if (candidates.length === 0) { setLoading(false); return; }

        // Rate-weighted draw among the non-capped candidates. This used to be
        // candidates.find(), which meant the newest-created ad won every time
        // for every user until it capped out, regardless of what it paid.
        const pick = AdService.pickWeightedAd(candidates);
        if (!cancelled && pick) setAd(pick);
      } catch (e) {
        console.warn('[AdUnit] Failed to load ad:', e.message);
      }
      if (!cancelled) setLoading(false);
    }

    loadAd();
    return () => { cancelled = true; };
  }, [placement, isPaidUser]);

  // Fire impression once, when ad is actually shown
  useEffect(() => {
    if (ad && !impressionFired.current) {
      impressionFired.current = true;
      AdService.recordAdShown(ad.id);
      fetch(`${API_URL}/api/ads/${ad.id}/impression`, { method: 'POST' }).catch(() => {});
      AdService.trackEvent(API_URL, { ad_id: ad.id, event_type: 'impression', placement });
    }
  }, [ad, placement]);

  const handleClick = () => {
    if (!ad) return;
    fetch(`${API_URL}/api/ads/${ad.id}/click`, { method: 'POST' }).catch(() => {});
    AdService.trackEvent(API_URL, { ad_id: ad.id, event_type: 'click', placement });
    window.open(ad.click_url || 'https://nethajiprime.com', '_blank', 'noopener,noreferrer');
  };

  if (loading || !ad) return null;

  const sizeByPlacement = {
    banner: { maxWidth: '100%', maxHeight: 120 },
    lband: { maxWidth: '100%', maxHeight: 60 },
    preroll: { maxWidth: '100%', maxHeight: 400 },
  };
  const dims = sizeByPlacement[placement] || sizeByPlacement.banner;

  const isVideo = /\.(mp4|webm)(\?|$)/i.test(ad.media_url || '');

  return (
    <div
      onClick={handleClick}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        margin: '12px 0',
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(212,168,67,0.1)',
      }}
    >
      <span style={{
        position: 'absolute', top: 4, left: 8, fontSize: 10,
        color: 'rgba(255,255,255,0.4)', letterSpacing: 1, zIndex: 1,
      }}>
        AD
      </span>
      {isVideo
        ? <video
            src={ad.media_url}
            muted autoPlay loop playsInline
            style={{ ...dims, width: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        : <img
            src={ad.media_url}
            alt={ad.title}
            style={{ ...dims, width: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />}
    </div>
  );
}
